import { useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { hideQueueItems, unhideQueueItems } from '../../shared/api/procurement.api'
import { toast } from '../../shared/ui/toastStore'
import type { QueueRow } from './queueTypes'

/**
 * Мутации per-user скрытия позиций очереди (только GENERAL_DIRECTOR): скрыть /
 * вернуть выделенные / вернуть все. Режим «Показать скрытые» (showHidden)
 * держится в самом ProcurementQueuePage, т.к. нужен до загрузки очереди.
 * Вынесено из страницы (файл за лимитом 500 строк).
 */
export function useQueueHide(params: {
  selectedRows: QueueRow[]
  invalidateQueue: () => void
  deselectAll: () => void
}) {
  const { selectedRows, invalidateQueue, deselectAll } = params

  const hideMut = useMutation({
    mutationFn: (ids: string[]) => hideQueueItems(ids),
    onSuccess: (res) => {
      invalidateQueue()
      deselectAll()
      toast.success(res.hidden > 0 ? `Скрыто позиций: ${res.hidden}` : 'Позиции уже были скрыты')
    },
    onError: () => toast.error('Не удалось скрыть позиции'),
  })

  const unhideMut = useMutation({
    mutationFn: (ids: string[] | undefined) => unhideQueueItems(ids),
    onSuccess: (res) => {
      invalidateQueue()
      deselectAll()
      toast.success(res.restored > 0 ? `Возвращено позиций: ${res.restored}` : 'Нечего возвращать')
    },
    onError: () => toast.error('Не удалось вернуть позиции'),
  })

  const hideSelected = useCallback(() => {
    const ids = selectedRows.map((r) => r._raw.requestItemId)
    if (ids.length === 0) return
    hideMut.mutate(ids)
  }, [selectedRows, hideMut])

  const unhideSelected = useCallback(() => {
    const ids = selectedRows.map((r) => r._raw.requestItemId)
    if (ids.length === 0) return
    unhideMut.mutate(ids)
  }, [selectedRows, unhideMut])

  const unhideAll = useCallback(() => unhideMut.mutate(undefined), [unhideMut])

  return {
    hideSelected,
    unhideSelected,
    unhideAll,
    busy: hideMut.isPending || unhideMut.isPending,
  }
}
